import { useQuery } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import type { CanvasserAnalyticsFilters } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import type { CanvasserStatsResponse, VendorSummaryItem, MyStatsResponse, CanvasserPerformanceResponse, VendorPerformanceResponse } from '@signalvote/shared';
import { buildQueryString } from './queryUtils';

export function useCanvasserStats(filters?: CanvasserAnalyticsFilters) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.analytics.canvasserStats(filters),
    queryFn: async () => {
      const qs = buildQueryString({
        type: filters?.type,
        vendorId: filters?.vendorId,
        teamId: filters?.teamId,
        userId: filters?.userId,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
      });
      return apiJson<CanvasserStatsResponse>(`/api/analytics/canvasser-stats${qs}`, { error: 'Failed to fetch canvasser stats' });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 2 * 60 * 1000,
  });
}

export function useVendorSummary(filters?: CanvasserAnalyticsFilters) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.analytics.vendorSummary(filters),
    queryFn: async () => {
      const qs = buildQueryString({
        type: filters?.type,
        vendorId: filters?.vendorId,
        teamId: filters?.teamId,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
      });
      return apiJson<VendorSummaryItem[]>(`/api/analytics/vendor-summary${qs}`, { error: 'Failed to fetch vendor summary' });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 2 * 60 * 1000,
  });
}

export function useMyStats(filters?: Pick<CanvasserAnalyticsFilters, 'dateFrom' | 'dateTo'>) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.analytics.myStats(filters),
    queryFn: async () => {
      const qs = buildQueryString({
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
      });
      return apiJson<MyStatsResponse>(`/api/analytics/my-stats${qs}`, { error: 'Failed to fetch my stats' });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

export function useCanvasserPerformance(userId: string) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.analytics.canvasserPerformance(userId),
    queryFn: async () => {
      const qs = buildQueryString({ userId });
      return apiJson<CanvasserPerformanceResponse>(`/api/analytics/canvasser-performance${qs}`, { error: 'Failed to fetch canvasser performance' });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 10 * 60 * 1000,
  });
}

export function useVendorPerformance(
  vendorId: string,
  filters?: Pick<CanvasserAnalyticsFilters, 'dateFrom' | 'dateTo'>
) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.analytics.vendorPerformance(vendorId, filters),
    queryFn: async () => {
      const qs = buildQueryString({
        vendorId,
        dateFrom: filters?.dateFrom,
        dateTo: filters?.dateTo,
      });
      return apiJson<VendorPerformanceResponse>(`/api/analytics/vendor-performance${qs}`, { error: 'Failed to fetch vendor performance' });
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: 10 * 60 * 1000,
  });
}
